// A price book item's history (pages/ux-full screens/setup.js, the item's History), drawn from
// pricebook.itemHistory's answer: newest first, one entry a save (the fields one save, import or refresh
// changed together), each field old to new, then who, how and why. One action an entry: "Put back these
// values" (pricebook.rollback), for those who may change the book. Our cost's changes come only to those
// who see cost, so nothing here hides them.
//
// The price book's pictures (pages/ux-full components/setup.js: PriceBookRow's picture, and the item's
// pictures in its sheet). A row's is small: the primary picture's thumbnail, or a dashed frame when the
// item has none, drawn (an svg), never an outlined box. The item's: its first picture large, the others
// small in their order with their captions, then its spec sheets as links to open. Which picture shows
// first is the screen's callback, drawn only when the screen passes it; where they came from (imported
// from the company's previous system, kept for reference) is the screen's words, said above them.
import type {
  ItemChange,
  ItemHistoryProps,
  ItemMediaEntry,
  ItemMediaProps,
  ItemPictureProps,
} from '../pricebook-schemas.ts';
import { clock, money } from '../format.ts';
import { Action } from '../primitives.tsx';

/** The order an entry lists its fields in, and each one's words. */
const FIELDS: [string, string][] = [
  ['name', 'Name'],
  ['code', 'Code'],
  ['type', 'Kind'],
  ['category_id', 'Category'],
  ['description', 'What it includes'],
  ['unit', 'Sold by'],
  ['price_cents', 'Price'],
  ['member_price_cents', 'Member price'],
  ['cost_cents', 'Our cost'],
  ['taxable', 'Taxed'],
  ['tax_class', 'Tax'],
  ['spiff_cents', 'Spiff'],
  ['active', 'Offered'],
  ['sellable', 'For sale'],
];
const LABEL = new Map(FIELDS);
const ORDER = new Map(FIELDS.map(([f], i) => [f, i]));
const MONEY = new Set(['price_cents', 'member_price_cents', 'cost_cents', 'spiff_cents']);
/** What a put-back can set (pricebook.rollback): a code, kind or for-sale flag stays as it is. */
const BY_HAND = new Set([
  'name',
  'category_id',
  'description',
  'unit',
  'price_cents',
  'member_price_cents',
  'cost_cents',
  'taxable',
  'tax_class',
  'spiff_cents',
  'active',
]);
/** A yes-or-no field's change, as one phrase: [became true, became false]. */
const FLAGS: Record<string, [string, string]> = {
  active: ['Offered again', 'No longer offered'],
  taxable: ['Taxed', 'No longer taxed'],
  sellable: ['For sale', 'No longer for sale'],
};
const HOW: Record<ItemChange['source'], string> = {
  manual: 'by hand',
  import: 'from the spreadsheet',
  servicetitan: 'ServiceTitan’s refresh',
  supplier_list: 'from a supplier’s list',
  draft: 'from an approved request',
  rollback: 'put back',
};

const LONG = 60;
function valueWords(c: ItemChange, v: ItemChange['old'], side: 'old' | 'new'): string {
  if (c.field === 'category_id')
    return v === null ? 'no category' : (c.category?.[side] ?? 'a category since removed');
  if (v === null) return 'none';
  if (MONEY.has(c.field) && typeof v === 'number') return money(v);
  const t = String(v);
  return `“${t.length > LONG ? `${t.slice(0, LONG - 1)}…` : t}”`;
}

/** One field's change in words: "Price $2,350.00 to $2,400.00", "No longer offered". */
export function changeWords(c: ItemChange): string {
  const flag = FLAGS[c.field];
  if (flag && typeof c.new === 'boolean') return c.new ? flag[0] : flag[1];
  return `${LABEL.get(c.field) ?? c.field} ${valueWords(c, c.old, 'old')} to ${valueWords(c, c.new, 'new')}`;
}

/** Who and how: "Pat H. · by hand", "ServiceTitan’s refresh" (the system has no name). */
export function howWords(c: ItemChange): string {
  return [c.by.name, HOW[c.source]].filter(Boolean).join(' · ');
}

/** The changes as entries: those one save made together (one batch, side by side) are one entry. */
export function changeEntries(changes: readonly ItemChange[]): ItemChange[][] {
  const out: ItemChange[][] = [];
  for (const c of changes) {
    const last = out[out.length - 1];
    if (last && c.batchId && last[0]!.batchId === c.batchId) last.push(c);
    else out.push([c]);
  }
  return out.map((e) => [...e].sort((a, b) => (ORDER.get(a.field) ?? 99) - (ORDER.get(b.field) ?? 99)));
}

/** An instant as the company's calendar and clock read it: "2 Oct 2026, 9:52 a.m.". */
function when(iso: string, timeZone: string): string {
  const p = new Intl.DateTimeFormat('en-CA', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone,
  }).formatToParts(new Date(iso));
  const part = (t: string) => p.find((x) => x.type === t)?.value ?? '';
  return `${part('day')} ${part('month').replace('.', '')} ${part('year')}, ${clock(iso, timeZone)}`;
}

export function ItemHistory({
  data,
  timeZone,
  onPutBack,
  onMore,
  busy = null,
}: {
  data: ItemHistoryProps;
  /** The company's zone: each change's date and time as the office reads them. */
  timeZone: string;
  /** Puts back the values one entry changed (pricebook.rollback), by its first change: the screen's. */
  onPutBack?: (changeId: string, entry: ItemChange[]) => void;
  /** The older changes (pricebook.itemHistory's next), when there are more. */
  onMore?: () => void;
  /** The entry being put back: nothing is pressed twice. */
  busy?: string | null;
}) {
  if (!data.changes.length)
    return (
      <p className="flw-empty" role="status">
        No changes kept for it yet. Every change from now on is kept here, with its old values.
      </p>
    );
  return (
    <>
      <ol className="flw-rows flw-history">
        {changeEntries(data.changes).map((entry) => {
          const first = entry[0]!;
          const at = when(first.at, timeZone);
          const canPutBack = onPutBack && entry.some((c) => BY_HAND.has(c.field));
          return (
            <li key={first.id} className="flw-row flw-change">
              <span>
                <span className="flw-sub">{at}</span>
                {entry.map((c) => (
                  <span key={c.id} className="flw-change-line">
                    {changeWords(c)}
                  </span>
                ))}
                <span className="flw-sub">
                  {howWords(first)}
                  {first.reason ? `: ${first.reason}` : ''}
                </span>
                {canPutBack && (
                  <span className="flw-acts">
                    <Action onClick={() => onPutBack(first.id, entry)} label={`Put back the values of ${at}`}>
                      {busy === first.id ? 'Putting back…' : 'Put back these values'}
                    </Action>
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ol>
      {data.next && onMore && (
        <p className="flw-more">
          <Action onClick={onMore}>Show older changes</Action>
        </p>
      )}
    </>
  );
}

type Size = 'row' | 'item';

/** No picture yet: a dashed frame the size of one. */
function NoPicture({ size }: { size: Size }) {
  return (
    <svg
      className={`flw-pic flw-pic-${size} flw-pic-none`}
      role="img"
      aria-label="No picture yet"
      viewBox="0 0 4 3"
      preserveAspectRatio="none"
    >
      <rect x="0" y="0" width="4" height="3" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/** A picture, or its place while its link isn't there yet (no bucket, a copy being made). */
function Picture({ src, words, size }: { src: string | null; words: string; size: Size }) {
  if (!src)
    return (
      <span className={`flw-pic flw-pic-${size}`} role="img" aria-label={`${words}, not ready to show`} />
    );
  return (
    <span className={`flw-pic flw-pic-${size}`}>
      <img src={src} alt={words} loading="lazy" />
    </span>
  );
}

/** A price book row's picture (pricebook.listItems' picture). */
export function ItemPicture({ data }: { data: ItemPictureProps }) {
  return data.picture ? (
    <Picture src={data.picture.thumbUrl} words={`Picture of ${data.name}`} size="row" />
  ) : (
    <NoPicture size="row" />
  );
}

const wordsOf = (name: string, m: ItemMediaEntry) =>
  m.caption ? `${name}: ${m.caption}` : `Picture of ${name}`;

/** An item's pictures and spec sheets (pricebook.getItem's media), in their order. */
export function ItemMedia({
  data,
  from,
  onFirst,
  busy = false,
}: {
  data: ItemMediaProps;
  /** Where they came from, in the screen's words ("Imported photo, kept for reference."). */
  from?: string;
  /** Show this picture first (pricebook.setMedia): the screen's, for one who may change the item. */
  onFirst?: (fileId: string) => void;
  /** A change is on its way: nothing can be pressed twice. */
  busy?: boolean;
}) {
  const photos = data.media.filter((m) => m.kind === 'photo');
  const docs = data.media.filter((m) => m.kind === 'document');
  const first = photos.find((m) => m.isPrimary) ?? photos[0];
  const rest = photos.filter((m) => m !== first);
  return (
    <div className="flw-pics">
      {from && <p className="flw-sub">{from}</p>}
      {first ? (
        <figure className="flw-pics-first">
          <Picture src={first.url ?? first.thumbUrl} words={wordsOf(data.name, first)} size="item" />
          {first.caption && <figcaption className="flw-sub">{first.caption}</figcaption>}
        </figure>
      ) : (
        <>
          <NoPicture size="item" />
          {!docs.length && <p className="flw-sub">No picture yet. It’s shown by its name.</p>}
        </>
      )}
      {rest.length > 0 && (
        <ul className="flw-pics-more" aria-label="Its other pictures">
          {rest.map((m) => (
            <li key={m.fileId}>
              <Picture src={m.thumbUrl} words={wordsOf(data.name, m)} size="row" />
              {m.caption && <span className="flw-sub">{m.caption}</span>}
              {onFirst && (
                <button
                  type="button"
                  className="flw-link"
                  disabled={busy}
                  aria-label={`Show ${m.caption ?? 'this picture'} first`}
                  onClick={() => onFirst(m.fileId)}
                >
                  Show first
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {docs.length > 0 && (
        <ul className="flw-rows flw-pics-docs" aria-label="Spec sheets">
          {docs.map((m) => (
            <li key={m.fileId} className="flw-row">
              {m.url ? (
                <a href={m.url} target="_blank" rel="noreferrer">
                  {m.caption ?? 'Spec sheet'}
                </a>
              ) : (
                <span>{m.caption ?? 'Spec sheet'}</span>
              )}
              <span className="flw-sub">PDF</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
